type Faq = { question: string; answer: string };

const strip = (s: string) =>
  s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();

/**
 * Haalt vraag/antwoord-paren uit de pagina:
 * 1. H1 als vraag (eindigt op "?") + de intro-alinea eronder
 * 2. Contentsecties met een H2-vraag (eindigt op "?") + de tekst eronder
 * 3. Veelgestelde vragen: <details><summary><h3>…</h3></summary><div>…</div></details>
 */
export const extractFaqsFromHtml = (html: string): Faq[] => {
  const out: Faq[] = [];
  const seen = new Set<string>();
  const add = (q: string, a: string) => {
    const question = strip(q);
    const answer = strip(a);
    const key = question.toLowerCase();
    if (!question.endsWith("?") || !answer || seen.has(key)) return;
    seen.add(key);
    out.push({ question, answer });
  };
  let m: RegExpExecArray | null;

  const h1 = /<h1[^>]*>([\s\S]*?)<\/h1>\s*<p[^>]*>([\s\S]*?)<\/p>/g;
  while ((m = h1.exec(html))) add(m[1], m[2]);

  const sec = /<section[^>]*>\s*<h2[^>]*>([\s\S]*?)<\/h2>([\s\S]*?)<\/section>/g;
  while ((m = sec.exec(html))) {
    if (m[2].includes("<details")) continue;
    add(m[1], m[2].replace(/<a[^>]*class="[^"]*inline-flex[^"]*"[\s\S]*?<\/a>/g, ""));
  }

  const det = /<details[\s\S]*?<h3[^>]*>([\s\S]*?)<\/h3>[\s\S]*?<\/summary>([\s\S]*?)<\/details>/g;
  while ((m = det.exec(html))) add(m[1], m[2]);
  return out;
};

export const FaqSchema = ({ html, faqs }: { html?: string; faqs?: Faq[] }) => {
  const items = faqs ?? (html ? extractFaqsFromHtml(html) : []);
  if (!items.length) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, "\\u003c") }}
    />
  );
};
